// Snap utilities for the editor
// All functions are pure and exported for testability

/**
 * Default configuration for snap behavior
 */
export const DEFAULT_SNAP_CONFIG = {
    snapThreshold: 15,          // pixels to trigger center alignment snap
    spacingSnapThreshold: 8,    // pixels to trigger spacing snap (stricter - for fine alignment)
    proximityRadius: 150,       // max distance to consider for alignment
    alignTolerance: 10,         // tolerance for considering boxes "aligned" on a line
    spacingTolerance: 8,        // tolerance for consistent spacing
    minSpacing: 20,             // minimum spacing to consider a valid line
};

/**
 * Get the center of a box in world coordinates
 * @param {{ transform: number[], width: number, height: number }} box
 * @returns {{ x: number, y: number }}
 */
export const getBoxCenter = (box) => {
    const [a, b, c, d, e, f] = box.transform;
    const halfW = (box.width || 0) / 2;
    const halfH = (box.height || 0) / 2;
    return {
        x: a * halfW + c * halfH + e,
        y: b * halfW + d * halfH + f
    };
};

/**
 * Calculate spacings between consecutive values
 * @param {number[]} values - Sorted values
 * @returns {number[]}
 */
export const calculateSpacings = (values) => {
    const spacings = [];
    for (let i = 1; i < values.length; i++) {
        spacings.push(values[i] - values[i - 1]);
    }
    return spacings;
};

/**
 * Find runs of consecutive consistent spacings
 * Requires at least 2 consecutive spacings (3 points) to form a valid run
 * @param {number[]} spacings
 * @param {number} tolerance
 * @returns {Array<{ startIdx: number, endIdx: number, avgSpacing: number }>}
 */
export const findConsistentRuns = (spacings, tolerance) => {
    const runs = [];
    if (spacings.length < 2) return runs;

    let runStart = 0;
    let runSum = spacings[0];
    let runCount = 1;

    for (let i = 1; i <= spacings.length; i++) {
        const runAvg = runSum / runCount;
        const runEnded = i === spacings.length ||
            Math.abs(spacings[i] - runAvg) > tolerance;

        if (runEnded && runCount >= 2) {
            runs.push({
                startIdx: runStart,
                endIdx: runStart + runCount,
                avgSpacing: runAvg
            });
        }

        if (i < spacings.length) {
            if (runEnded) {
                runStart = i;
                runSum = spacings[i];
                runCount = 1;
            } else {
                runSum += spacings[i];
                runCount++;
            }
        }
    }

    return runs;
};

/**
 * Find all lines (any direction) with evenly spaced boxes
 * Returns extension points for snapping
 * @param {Array<{x: number, y: number}>} centers
 * @param {object} config
 * @returns {Array<{ startExt: {x, y}, endExt: {x, y}, spacing: number }>}
 */
export const findAllLines = (centers, config = DEFAULT_SNAP_CONFIG) => {
    const { alignTolerance, spacingTolerance, minSpacing } = config;
    const lines = [];
    const foundLines = new Set(); // Track found lines to avoid duplicates

    if (centers.length < 3) return lines;

    // For each pair of points, check if there are other points on that line
    for (let i = 0; i < centers.length; i++) {
        for (let j = i + 1; j < centers.length; j++) {
            const p1 = centers[i];
            const p2 = centers[j];

            // Direction vector from p1 to p2
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len < minSpacing) continue;

            // Unit direction vector
            const ux = dx / len;
            const uy = dy / len;

            // Find all points on this line
            const pointsOnLine = [];
            for (const p of centers) {
                // Distance from p to the line through p1 and p2
                // Using cross product: |((p - p1) × (p2 - p1))| / |p2 - p1|
                const cross = (p.x - p1.x) * dy - (p.y - p1.y) * dx;
                const distToLine = Math.abs(cross) / len;

                if (distToLine < alignTolerance) {
                    // Project p onto the line to get its position along the line
                    const t = (p.x - p1.x) * ux + (p.y - p1.y) * uy;
                    pointsOnLine.push({ x: p.x, y: p.y, t });
                }
            }

            if (pointsOnLine.length < 3) continue;

            // Sort by position along the line
            pointsOnLine.sort((a, b) => a.t - b.t);

            // Calculate spacings along the line
            const positions = pointsOnLine.map(p => p.t);
            const spacings = calculateSpacings(positions);

            // Find consistent spacing runs
            const runs = findConsistentRuns(spacings, spacingTolerance);

            for (const run of runs) {
                if (run.avgSpacing < minSpacing) continue;

                const runPoints = pointsOnLine.slice(run.startIdx, run.endIdx + 1);
                if (runPoints.length < 3) continue;

                const firstT = runPoints[0].t;
                const lastT = runPoints[runPoints.length - 1].t;

                // Extension points along the line direction
                const startExt = {
                    x: p1.x + ux * (firstT - run.avgSpacing),
                    y: p1.y + uy * (firstT - run.avgSpacing)
                };
                const endExt = {
                    x: p1.x + ux * (lastT + run.avgSpacing),
                    y: p1.y + uy * (lastT + run.avgSpacing)
                };

                // Create a key to identify this line (round to avoid float issues)
                const startKey = `${Math.round(startExt.x)},${Math.round(startExt.y)}`;
                const endKey = `${Math.round(endExt.x)},${Math.round(endExt.y)}`;
                const lineKey = startKey < endKey ? `${startKey}-${endKey}` : `${endKey}-${startKey}`;

                if (!foundLines.has(lineKey)) {
                    foundLines.add(lineKey);
                    lines.push({
                        startExt,
                        endExt,
                        spacing: run.avgSpacing
                    });
                }
            }
        }
    }

    return lines;
};

/**
 * Find line spacing snap - works for any line direction (horizontal, vertical, diagonal)
 * Uses stricter threshold than center alignment for fine positioning
 * @param {{ x: number, y: number }} draggedCenter
 * @param {Array<{x: number, y: number}>} otherCenters
 * @param {object} config
 * @returns {{ x: number, y: number } | null}
 */
export const findLineSpacingSnap = (draggedCenter, otherCenters, config = DEFAULT_SNAP_CONFIG) => {
    const { spacingSnapThreshold } = config;

    const lines = findAllLines(otherCenters, config);

    let closestSnap = null;
    let closestDistance = Infinity;

    for (const line of lines) {
        const vx = line.endExt.x - line.startExt.x;
        const vy = line.endExt.y - line.startExt.y;
        const lineLengthSq = vx * vx + vy * vy;

        if (lineLengthSq === 0) {
            continue;
        }

        // Project dragged center onto the infinite line
        const wx = draggedCenter.x - line.startExt.x;
        const wy = draggedCenter.y - line.startExt.y;
        const t = (wx * vx + wy * vy) / lineLengthSq;

        // Clamp projection to the segment so we can measure perpendicular distance reliably
        const clampedT = Math.min(1, Math.max(0, t));
        const projX = line.startExt.x + clampedT * vx;
        const projY = line.startExt.y + clampedT * vy;

        // Perpendicular distance from the dragged center to the line
        const perpDist = Math.sqrt(
            Math.pow(draggedCenter.x - projX, 2) +
            Math.pow(draggedCenter.y - projY, 2)
        );

        if (perpDist < spacingSnapThreshold && perpDist < closestDistance) {
            // Prefer exact extension points when close to either end to preserve spacing
            const distToStart = Math.sqrt(
                Math.pow(draggedCenter.x - line.startExt.x, 2) +
                Math.pow(draggedCenter.y - line.startExt.y, 2)
            );
            const distToEnd = Math.sqrt(
                Math.pow(draggedCenter.x - line.endExt.x, 2) +
                Math.pow(draggedCenter.y - line.endExt.y, 2)
            );

            if (distToStart < spacingSnapThreshold) {
                closestSnap = { x: line.startExt.x, y: line.startExt.y };
                closestDistance = distToStart;
            } else if (distToEnd < spacingSnapThreshold) {
                closestSnap = { x: line.endExt.x, y: line.endExt.y };
                closestDistance = distToEnd;
            } else {
                closestSnap = { x: projX, y: projY };
                closestDistance = perpDist;
            }
        }
    }

    return closestSnap;
};

/**
 * Find center alignment snap (snap to align with another box's center)
 */
export const findCenterAlignmentSnap = (draggedCenter, otherCenters, config = DEFAULT_SNAP_CONFIG) => {
    const { snapThreshold, proximityRadius } = config;
    let snapX = null;
    let snapY = null;

    for (const otherCenter of otherCenters) {
        const diffX = Math.abs(draggedCenter.x - otherCenter.x);
        const diffY = Math.abs(draggedCenter.y - otherCenter.y);

        // For X alignment: boxes must be close vertically
        if (snapX === null && diffX < snapThreshold && diffY < proximityRadius) {
            snapX = otherCenter.x;
        }
        // For Y alignment: boxes must be close horizontally
        if (snapY === null && diffY < snapThreshold && diffX < proximityRadius) {
            snapY = otherCenter.y;
        }

        if (snapX !== null && snapY !== null) break;
    }

    return { x: snapX, y: snapY };
};

/**
 * Calculate snap adjustments for dragged boxes
 * Priority: 1. Center alignment (more common), 2. Line spacing snap (fine positioning)
 */
export const calculateSnap = (draggedCenters, otherCenters, dx, dy, config = DEFAULT_SNAP_CONFIG) => {
    let snapX = null;
    let snapY = null;
    let snapLineX = null;
    let snapLineY = null;

    // === CENTER ALIGNMENT SNAP (priority - more common use case) ===
    for (const draggedCenter of draggedCenters) {
        const alignSnap = findCenterAlignmentSnap(draggedCenter, otherCenters, config);

        if (snapX === null && alignSnap.x !== null) {
            snapX = alignSnap.x - draggedCenter.x + dx;
            snapLineX = alignSnap.x;
        }
        if (snapY === null && alignSnap.y !== null) {
            snapY = alignSnap.y - draggedCenter.y + dy;
            snapLineY = alignSnap.y;
        }

        if (snapX !== null && snapY !== null) break;
    }

    // === LINE SPACING SNAP (stricter threshold - for fine even-spacing positioning) ===
    for (const draggedCenter of draggedCenters) {
        if (snapX === null || snapY === null) {
            const lineSnap = findLineSpacingSnap(draggedCenter, otherCenters, config);
            if (lineSnap) {
                if (snapX === null) {
                    snapX = lineSnap.x - draggedCenter.x + dx;
                    snapLineX = lineSnap.x;
                }
                if (snapY === null) {
                    snapY = lineSnap.y - draggedCenter.y + dy;
                    snapLineY = lineSnap.y;
                }
            }
        }
        if (snapX !== null && snapY !== null) break;
    }

    return {
        dx: snapX !== null ? snapX : dx,
        dy: snapY !== null ? snapY : dy,
        snapLineX,
        snapLineY
    };
};
